import React, { useEffect } from 'react';

import {
  DATA_TABLE_SOURCE_SUNSETUP_DRIVER_ORDERS,
  DATA_TABLE_SOURCE_SUNSETUP_LISTING_ORDERS,
} from 'common/dataTableConstants';

import { LISTING_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS } from 'common/listingConstants';

import {
  JOB_TYPE_CODEBOX_INSTALL,
  JOB_TYPE_COMBO_LOCK_INSTALL,
  JOB_TYPE_SUNLOCK_INSTALL,
  JOB_TYPE_SUPRA_LOCK_INSTALL,
} from 'common/jobConstants';

import {
  INVENTORY_ITEM_TYPE_CODEBOX_HOMERIVER,
  INVENTORY_ITEM_TYPE_RASPBERRY_PI,
} from 'common/inventoryConstants';

import { hasRoleAdmin } from 'common/userRoles';

import {
  SR_API_ACTION_SET_REDIRECTION,
  SR_API_ACTION_GET_REDIRECTION_BY_LISTING_ID,
  SR_API_ACTION_DELETE_REDIRECTION,
  SR_API_ACTION_DRIVER_UPDATE_LISTING,
  SR_API_ACTION_SUNLOCK_GET_BY_SERIAL_NUMBER,
  SR_API_ACTION_CODEBOX_GET_BY_SERIAL_NUMBER,
  SR_API_ACTION_CODEBOX_ASSIGN_LISTING_ID,
  SR_API_ACTION_INVENTORY_ITEM_GROUP_SCAN,
  SR_API_ACTION_INVENTORY_ITEM_SCAN,
} from 'api/actionTypes';

import { actionDataTableReset } from 'client/helpers/dataTables';

import useBranch from 'client/hooks/useBranch';
import JobActions from 'client/actions/Job';
import SunlockActions from 'client/actions/Sunlock';
import postWithState from 'client/actions/postWithState';

import SrFlex from 'client/components/SrFlex';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrSunSetupDriverJobPage from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobPage';

function getDataTableSettingsFromProps({ driverId, orderId }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_SUNSETUP_DRIVER_ORDERS,
    rootViewId: DATA_TABLE_SOURCE_SUNSETUP_DRIVER_ORDERS,
    initialViewSettings: {
      filter: {
        'jobOrders.id': orderId,
        'jobOrders.driverId': driverId,
      },
      sort: {
        'jobOrders.createdDate': 'desc',
      },
    },
    count: 1,
    itemsPropName: 'jobOrders',
  };

  return dataTableSettings;
}

const SrSunSetupDriverJob = ({
  driverId,
  orderId,
  jobId,
  history,
  requestGeolocationPermission,
  getCurrentLocation,
}) => {
  const { dispatch, currentUser, userAgent, appUrl } = useBranch({
    userAgent: ['analytics', 'userAgent'],
    appUrl: ['env', 'APP_URL'],
    currentUser: ['currentUser'],
  });

  let _reloadJobOrder;

  useEffect(() => {
    const interval = global.setInterval(() => {
      if (_reloadJobOrder) {
        _reloadJobOrder();
      }
    }, 60000);
    return () => {
      global.clearInterval(interval);
    };
  });

  return (
    <SrDataTableConnected {...getDataTableSettingsFromProps({ driverId, orderId })}>
      {({ jobOrders, dataTableApiState, reloadItems, onInfiniteScrollWrapperRef }) => {
        const jobOrder = jobOrders[0];
        const job = jobOrder && jobOrder.jobs.find((job) => job.id === jobId);

        const reloadJobOrder = () => {
          reloadItems({
            count: 1,
            filter: {
              'jobOrders.id': orderId,
            },
          });
        };

        _reloadJobOrder = reloadJobOrder;

        const onUpdateJob = ({ updates }) => {
          return dispatch(JobActions.update, {
            jobId,
            updates,
          })
            .then(() => {
              return dispatch(actionDataTableReset, {
                dataSource: DATA_TABLE_SOURCE_SUNSETUP_LISTING_ORDERS,
              });
            })
            .then(reloadJobOrder);
        };

        const onUpdateListing = ({ updates }) => {
          return dispatch(postWithState, {
            action: SR_API_ACTION_DRIVER_UPDATE_LISTING,
            payload: {
              listingId: jobOrder.listingId,
              updates,
            },
          }).then(reloadJobOrder);
        };

        const handleSunlockRegistration = ({ itemGroup = [], latitude, longitude }) => {
          let _sunlock = null;
          return Promise.resolve()
            .then(() => {
              const item = itemGroup.find((item) => item.type === INVENTORY_ITEM_TYPE_RASPBERRY_PI);
              if (item && item.serialNumber) {
                return dispatch(postWithState, {
                  action: SR_API_ACTION_SUNLOCK_GET_BY_SERIAL_NUMBER,
                  payload: {
                    serialNumber: item.serialNumber,
                  },
                });
              }
            })
            .then(async (sunlock) => {
              if (sunlock) {
                await dispatch(postWithState, {
                  action: SR_API_ACTION_INVENTORY_ITEM_GROUP_SCAN,
                  payload: {
                    itemGroup,
                    latitude,
                    longitude,
                    jobId: job?.id,
                    ...(job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL && {
                      listingId: jobOrder?.listing?.id,
                    }),
                  },
                });

                if (job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL) {
                  _sunlock = sunlock;
                  return dispatch(SunlockActions.assign, {
                    listingId: jobOrder.listing.id,
                    sunlockId: sunlock.id,
                    triggeredBy: 'Driver Dashboard',
                  });
                }
              }
            })
            .then(reloadJobOrder)
            .then(() => _sunlock);
        };

        const handleCodeBoxRegistration = ({ type, serialNumber, latitude, longitude, status }) => {
          let _codeboxLock = null;
          return Promise.resolve()
            .then(() => {
              if (serialNumber) {
                return dispatch(postWithState, {
                  action: SR_API_ACTION_CODEBOX_GET_BY_SERIAL_NUMBER,
                  payload: {
                    serialNumber,
                  },
                });
              }
            })
            .then(async (codeboxLock) => {
              if (type === INVENTORY_ITEM_TYPE_CODEBOX_HOMERIVER) {
                await dispatch(postWithState, {
                  action: SR_API_ACTION_INVENTORY_ITEM_SCAN,
                  payload: {
                    type,
                    serialNumber,
                    latitude,
                    longitude,
                    status,
                    jobId: job?.id,
                    ...(job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && {
                      listingId: jobOrder?.listing?.id,
                    }),
                  },
                });
              }

              if (codeboxLock) {
                _codeboxLock = codeboxLock;

                await dispatch(postWithState, {
                  action: SR_API_ACTION_INVENTORY_ITEM_SCAN,
                  payload: {
                    type,
                    serialNumber,
                    latitude,
                    longitude,
                    status,
                    jobId: job?.id,
                    ...(job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && {
                      listingId: jobOrder?.listing?.id,
                    }),
                  },
                });

                if (job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL) {
                  return dispatch(postWithState, {
                    action: SR_API_ACTION_CODEBOX_ASSIGN_LISTING_ID,
                    payload: {
                      codeboxLockId: codeboxLock.id,
                      triggeredBy: 'Driver Dashboard',
                      listingId: jobOrder.listing.id,
                    },
                  });
                }
              }
            })
            .then(reloadJobOrder)
            .then(() => _codeboxLock);
        };

        const handleYardSignTakeDownScan = ({ itemGroup, latitude, longitude }) => {
          return Promise.resolve()
            .then(() => {
              return dispatch(postWithState, {
                action: SR_API_ACTION_INVENTORY_ITEM_GROUP_SCAN,
                payload: {
                  itemGroup,
                  latitude,
                  longitude,
                  jobId: job?.id,
                },
              });
            })
            .then(reloadJobOrder);
        };

        const handleYardSignRegistration = ({ itemGroup, yardSignUrl, latitude, longitude }) => {
          return Promise.resolve()
            .then(() => {
              return dispatch(postWithState, {
                action: SR_API_ACTION_INVENTORY_ITEM_GROUP_SCAN,
                payload: {
                  itemGroup,
                  latitude,
                  longitude,
                  listingId: jobOrder?.listing?.id,
                  jobId: job?.id,
                },
              });
            })
            .then(() => {
              return dispatch(postWithState, {
                action: SR_API_ACTION_GET_REDIRECTION_BY_LISTING_ID,
                payload: {
                  listingId: jobOrder.listing.id,
                },
              });
            })
            .then((listingRedirection) => {
              if (listingRedirection) {
                return dispatch(postWithState, {
                  action: SR_API_ACTION_DELETE_REDIRECTION,
                  payload: {
                    id: listingRedirection.id,
                  },
                });
              }
            })
            .then(() => {
              if (yardSignUrl) {
                const redirectionSourceUrl = yardSignUrl.split('/').pop().trim().toUpperCase();
                return dispatch(postWithState, {
                  action: SR_API_ACTION_SET_REDIRECTION,
                  payload: {
                    id: redirectionSourceUrl,
                    url: `/listing/${jobOrder.listingId}?${LISTING_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS}`,
                  },
                });
              }
            })
            .then(reloadJobOrder)
            .then(() => yardSignUrl);
        };

        const handleComboLockboxRegistration = ({
          type,
          serialNumber,
          latitude,
          longitude,
          status,
        }) => {
          return Promise.resolve()
            .then(() => {
              return dispatch(postWithState, {
                action: SR_API_ACTION_INVENTORY_ITEM_SCAN,
                payload: {
                  type,
                  serialNumber,
                  latitude,
                  longitude,
                  status,
                  jobId: job?.id,
                  ...(job.jobTypeId === JOB_TYPE_COMBO_LOCK_INSTALL && {
                    listingId: jobOrder?.listing?.id,
                  }),
                },
              });
            })
            .then(reloadJobOrder);
        };

        const handleSupraRegistration = ({ type, serialNumber, latitude, longitude, status }) => {
          return Promise.resolve()
            .then(() => {
              return dispatch(postWithState, {
                action: SR_API_ACTION_INVENTORY_ITEM_SCAN,
                payload: {
                  type,
                  serialNumber,
                  latitude,
                  longitude,
                  status,
                  jobId: job?.id,
                  ...(job.jobTypeId === JOB_TYPE_SUPRA_LOCK_INSTALL && {
                    listingId: jobOrder?.listing?.id,
                  }),
                },
              });
            })
            .then(reloadJobOrder);
        };

        return (
          <>
            <div ref={onInfiniteScrollWrapperRef}>
              {job && (
                <SrSunSetupDriverJobPage
                  userAgent={userAgent}
                  job={job}
                  jobOrderId={orderId}
                  driverId={driverId}
                  history={history}
                  listing={jobOrder.listing}
                  sunlock={jobOrder.sunlock}
                  codeboxLock={jobOrder.codeboxLock}
                  yardSignUrl={jobOrder.yardSignUrl}
                  onUpdateJob={onUpdateJob}
                  onUpdateListing={onUpdateListing}
                  handleSunlockRegistration={handleSunlockRegistration}
                  handleYardSignRegistration={handleYardSignRegistration}
                  handleYardSignTakeDownScan={handleYardSignTakeDownScan}
                  handleCodeBoxRegistration={handleCodeBoxRegistration}
                  handleComboLockboxRegistration={handleComboLockboxRegistration}
                  handleSupraRegistration={handleSupraRegistration}
                  appUrl={appUrl}
                  isAdmin={hasRoleAdmin(currentUser.roles)}
                  requestGeolocationPermission={requestGeolocationPermission}
                  getCurrentLocation={getCurrentLocation}
                />
              )}
              {dataTableApiState.inProgress && (
                <SrFlex justify="center">
                  <div className="loading-gif" />
                </SrFlex>
              )}
            </div>
          </>
        );
      }}
    </SrDataTableConnected>
  );
};

SrSunSetupDriverJob.displayName = 'SrSunSetupDriverJob';

export default SrSunSetupDriverJob;
